import { Editor, Raw, Plain } from 'slate'
import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Container, ButtonToolbar, ButtonGroup } from 'reactstrap'
import deepEqual from 'deep-equal'
import AlertContainer from 'react-alert'
import { alertOptions } from '../../constants'
import styles from './SlateEditor.scss'

import UndoSlateButton from '../UndoSlateButton'
import { BoldSlatePlugin, BoldSlateButton } from '../BoldSlatePlugin'
import { ItalicSlatePlugin, ItalicSlateButton } from '../ItalicSlatePlugin'
import { SuperscriptSlatePlugin, SuperscriptSlateButton } from '../SuperscriptSlatePlugin'
import { LightSlatePlugin } from '../LightSlatePlugin'
import { UnderlineSlatePlugin, UnderlineSlateButton } from '../UnderlineSlatePlugin'
import { ListSlatePlugin, ListSlateButton } from '../ListSlatePlugin'
import { LinkSlatePlugin, LinkSlateButton } from '../LinkSlatePlugin'
import { DeepTableSlatePlugin } from '../DeepTableSlatePlugin'
import { ImageSlatePlugin, ImageSlateButton } from '../ImageSlatePlugin'
import { HorizontalLineSlatePlugin, HorizontalLineSlateButton } from '../HorizontalLineSlatePlugin'
import { FileSlatePlugin, FileSlateButton } from '../FileSlatePlugin'
import PasteImageSlatePlugin from '../PasteImageSlatePlugin'

import { WidgetSlatePlugin, WidgetSlateButton } from '../WidgetSlatePlugin'
import { TextStylesSlatePlugin, TextStylesSlateButton } from '../TextStylesSlatePlugin'
import { TextColorsSlatePlugin, TextColorsSlateButton } from '../TextColorsSlatePlugin'

const AccordionSlatePlugin = DeepTableSlatePlugin({isAccordion: true})
const AccordionSlateButton = AccordionSlatePlugin.AccordionButton

const GridSlatePlugin = DeepTableSlatePlugin({isGrid: true})
const GridSlateButton = GridSlatePlugin.GridButton

const TableSlatePlugin = DeepTableSlatePlugin({isTable: true})
const TableSlateButton = TableSlatePlugin.TableButton

const plugins = [
  BoldSlatePlugin(),
  ItalicSlatePlugin(),
  SuperscriptSlatePlugin(),
  LightSlatePlugin(),
  UnderlineSlatePlugin(),
  WidgetSlatePlugin(),
  TextStylesSlatePlugin(),
  TextColorsSlatePlugin(),
  TableSlatePlugin,
  AccordionSlatePlugin,
  GridSlatePlugin,
  HorizontalLineSlatePlugin(),
  LinkSlatePlugin(),
  ImageSlatePlugin(),
  FileSlatePlugin(),
  ListSlatePlugin(),
  PasteImageSlatePlugin()
]

const schema = {}

class SlateEditorContainer extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      state: Plain.deserialize(''),
      readOnly: false,
      focused: false
    }
  }

  componentDidMount = () => {
    let state
    try {
      state = this.props.content
        ? Raw.deserialize(this.props.content, { terse: true })
        : Plain.deserialize('')
    } catch (err) {
      console.info('Error with saved content', JSON.stringify(this.props.content, null, 2))
      this.msg.show('Error loading saved content', {
        time: 4000,
        type: 'error'
      })
      state = Plain.deserialize('')
    }

    this.setState({ state })
    window.addEventListener('scroll', this.onScroll)
  }

  setEditorReadOnly = bool => {
    this.setState({
      readOnly: bool
    })
  }

  onChange = (state) => {
    if (!state.document.nodes.size) {
      state = Plain.deserialize('')
    }
    const eq = deepEqual(this.state.state.document, state.document)
    this.setState({ state })
    if (this.isSafeToSave(state)) {
      const serialized = Raw.serialize(state, { normalize: false })
      if (!eq) {
        this.props.onDocumentChange(serialized)
      }
    }
  }

  isSafeToSave = (state) => {
    let startNode = state.document
    let openList = [startNode]

    while (openList.length) {
      let node = openList.shift()
      let data = node.data

      // don't save while modal open
      // or has placeholder ie uploading file
      if (data && (data.get('openModal') || data.get('isPlaceHolder'))) {
        return false
      }

      if (!node.nodes) continue

      for (let child of node.nodes) {
        if (child) {
          openList.push(child)
        }
      }
    }

    return true
  }

  onFocus = () => {
    this.setState({ focused: true })
  }
  onBlur = () => {
    this.setState({ focused: false })
  }

  renderToolbar = () => {
    return (
      <div
        /* data-slate-spacer is an undocumented slate flag that prevents
        the slate editor from losing focus when the toolbar is clicked
        see: https://github.com/ianstormtaylor/slate/blob/816c981436c909d3e24ff615d75cc16a8bc5645d/packages/slate-react/src/plugins/before.js#L94 */
        data-slate-spacer
        onMouseDown={e => e.preventDefault()}
        className={classNames(styles.toolbar, {
          [styles.focused]: this.state.focused
        })}>
        <Container>
          <ButtonToolbar>
            <ButtonGroup>
              <BoldSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <ItalicSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <UnderlineSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <SuperscriptSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <TextColorsSlateButton state={this.state.state} onChange={this.onChange} />
              <TextStylesSlateButton state={this.state.state} onChange={this.onChange} />
            </ButtonGroup>
            <ButtonGroup>
              <ListSlateButton state={this.state.state} onChange={this.onChange} listType='ul_list' noTooltip={this.props.isTestEnv} />
              <ListSlateButton state={this.state.state} onChange={this.onChange} listType='ol_list' noTooltip={this.props.isTestEnv} />
            </ButtonGroup>
            <ButtonGroup>
              <LinkSlateButton state={this.state.state}
                match={this.props.match}
                history={this.props.history}
                onChange={this.onChange}
                noTooltip={this.props.isTestEnv} />
              <FileSlateButton state={this.state.state}
                onChange={this.onChange}
                noTooltip={this.props.isTestEnv}
                match={this.props.match}
                setEditorReadOnly={this.setEditorReadOnly}
                history={this.props.history}
                />
              <ImageSlateButton state={this.state.state}
                onChange={this.onChange}
                noTooltip={this.props.isTestEnv}
                match={this.props.match}
                history={this.props.history}
                setEditorReadOnly={this.setEditorReadOnly}
                />
              <HorizontalLineSlateButton state={this.state.state}
                onChange={this.onChange}
                noTooltip={this.props.isTestEnv}
                match={this.props.match}
                history={this.props.history}
                setEditorReadOnly={this.setEditorReadOnly}
                />
              <TableSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <AccordionSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <GridSlateButton state={this.state.state} onChange={this.onChange} noTooltip={this.props.isTestEnv} />
              <WidgetSlateButton state={this.state.state} onChange={this.onChange} />
            </ButtonGroup>
            <ButtonGroup>
              <UndoSlateButton state={this.state.state} onChange={this.onChange} />
              <UndoSlateButton isRedo state={this.state.state} onChange={this.onChange} />
            </ButtonGroup>
          </ButtonToolbar>
        </Container>
      </div>
    )
  }

  getReadOnlyState = () => {
    // parent read only state takes priority
    return this.props.readOnly || this.state.readOnly
  }

  componentWillReceiveProps = (nextProps) => {
    let content = this.props.content

    if (this.props.didModifySections && content) {
      content = content && Raw.deserialize(content)
      // server manipulated the content
      this.setState({state: content})
      this.props.onResetDidModifySections()
    }
  }

  renderEditor = () => {
    const editorState = this.state.state

    return (
      <div className={`editor ${styles.SlateEditor} ` + (this.getReadOnlyState() ? styles.readOnly : '')}>
        {
          !this.props.isTestEnv &&
          <Editor
            spellCheck
            focused={this.state.focused}
            pageId={this.props.pageId}
            parentId={this.props.parentId}
            placeholder={'Enter some rich text...'}
            schema={schema}
            plugins={plugins}
            state={editorState}
            onChange={this.onChange}
            onKeyDown={this.onKeyDown}
            readOnly={this.getReadOnlyState()}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          />
        }
        {false && <div className='debug-block'>
          <pre>{JSON.stringify(Raw.serialize(this.state.state), null, 2)}</pre>
        </div>}
      </div>
    )
  }

  render () {
    return (
      <div className={styles.editorWrapper}>
        <AlertContainer ref={a => (this.msg = a)} {...alertOptions} />
        {this.renderToolbar()}
        {this.renderEditor()}
      </div>
    )
  }
}

SlateEditorContainer.propTypes = {
  pageId: PropTypes.number,
  parentId: PropTypes.number,
  didModifySections: PropTypes.bool,
  readOnly: PropTypes.bool,
  onResetDidModifySections: PropTypes.func,
  match: PropTypes.object,
  history: PropTypes.object,
  content: PropTypes.object,
  onDocumentChange: PropTypes.func,
  isTestEnv: PropTypes.bool
}

export default SlateEditorContainer



// WEBPACK FOOTER //
// ./src/components/SlateEditor/SlateEditorContainer.js